import { useState } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import { formatRoute } from '../app/router';
import { text } from '../app/i18n';
import { Drawer } from '../design-system/Overlay';
import { errorView, Loading } from '../states/States';
import { DetailRepresentation } from './DetailRepresentation';
import { useLoad } from './useLoad';
import { useViewServices } from './ViewPage';
import { AppServicesContext, useAppServices } from './ViewContext';

/**
 * Fiche d'un objet en tiroir, par-dessus l'écran qui l'ouvre (explorateur du graphe, ADR-129) : la même
 * vue résolue par `GetView` et le même `DetailRepresentation` que le tiroir d'une liste, sans quitter
 * l'écran. Fermer rend l'écran tel quel ; « Ouvrir en pleine page » mène à la liste du type, fiche ouverte.
 */
export function RecordDrawer({ objectType, recordId, title, onClose }: { objectType: string; recordId: string; title?: string | undefined; onClose: () => void }) {
  const services = useAppServices();
  const [resolution, reload] = useLoad<ViewResolution>(`record-drawer:${objectType}`, async () => (await services.client.query<ViewResolution>('GetView', { object_type: objectType })).data);
  const { scoped } = useViewServices(resolution);
  // Une modification faite depuis la fiche la relit.
  const [refresh, setRefresh] = useState(0);
  const object = resolution.status === 'ready' ? resolution.value.object : undefined;
  const page = { kind: 'object' as const, object_type: objectType, record_id: recordId };
  return (
    <Drawer
      title={object === undefined ? (title ?? 'Fiche') : `${text(object.label)}${title === undefined ? '' : ` ${title}`}`}
      open
      onClose={onClose}
      footer={
        <a
          className="ay-button ay-button--ghost"
          href={formatRoute(page)}
          onClick={(event) => {
            event.preventDefault();
            services.navigate(page);
          }}
        >
          Ouvrir en pleine page
        </a>
      }
    >
      {resolution.status === 'loading' && <Loading />}
      {resolution.status === 'error' && errorView(resolution.error, reload)}
      {resolution.status === 'ready' && (
        <AppServicesContext.Provider value={scoped}>
          <DetailRepresentation key={`${recordId}:${refresh}`} view={resolution.value.view.body} object={object} recordId={recordId} onChanged={() => setRefresh((value) => value + 1)} />
        </AppServicesContext.Provider>
      )}
    </Drawer>
  );
}

